import { gql } from '@apollo/client';
import { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { useNotificationContext } from 'core/context/Notifications';
import { useSkipToChallenge } from 'play/support/skipToChallenge';
import { useCustomParameters } from 'play/support/useCustomParameters';
import { useTrackAmplitudeEvent } from 'support/amplitude/amplitudeEvent';
import { addInvitePrefix, createPathPrefix, pathPrefixByMode } from 'support/location';
import { QueryParams } from 'support/QueryParams';
import { Mode, ProgressModalChallengeFragment, ProgressModalTrackFragment, useStopTrackMutation } from 'types/graphql';

import { LoadingModal } from './LoadingModal';
import { ProgressModal } from './ProgressModal';
import { RestartModal } from './RestartModal';
import { SkipModal } from './SkipModal';

export interface ProgressModalContainerProps {
  open: boolean;
  onClose: () => void;
  mode: Mode;
  teamSlug: string;
  skippingEnabled: boolean;
  track: ProgressModalTrackFragment;
  challenges: ProgressModalChallengeFragment[];
  currentChallenge?: ProgressModalChallengeFragment;
  loading: boolean;
  trackReviewsEnabled?: boolean;
}

export const ProgressModalContainer = ({
  open,
  onClose,
  mode,
  teamSlug,
  challenges,
  track,
  skippingEnabled,
  currentChallenge,
  loading,
  trackReviewsEnabled,
}: ProgressModalContainerProps) => {
  const history = useHistory();
  const { inviteID } = useParams<{ inviteID?: string }>();
  const [customParameters] = useCustomParameters();
  const { dispatch } = useNotificationContext();
  const [stopTrack, { loading: isStopping }] = useStopTrackMutation();

  const [restartOpen, setRestartOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [challengeSelectedId, setChallengeSelectedId] = useState<string>();
  const { status: trackStatus, slug: trackSlug } = track;

  const trackAmplitudeEvent = useTrackAmplitudeEvent();

  const { skipToChallenge, loading: isSkipping } = useSkipToChallenge({
    teamSlug,
    track,
    challenge: currentChallenge,
    nextChallenge: challenges.find(c => c.id === challengeSelectedId)!,
    mode,
    inviteID,
    trackReviewsEnabled,
    totalChallenges: challenges.length,
  });

  const onSkipConfirmed = () => {
    trackAmplitudeEvent({ name: 'Lab - Confirmed Skip' });
    challengeSelectedId && skipToChallenge(challengeSelectedId);
  };

  const openConfirmSkip = ({ id }: ProgressModalChallengeFragment) => {
    setChallengeSelectedId(id);
    setConfirmOpen(true);
    trackAmplitudeEvent({ name: 'Lab - Clicked Skip to' });
  };

  const onView = ({ slug }: ProgressModalChallengeFragment) =>
    history.push(
      `${createPathPrefix({ mode, inviteID })}/${teamSlug}/tracks/${trackSlug}/challenges/${slug}/assignment`,
      history.location.state,
    );

  const onContinue = ({ slug }: ProgressModalChallengeFragment) =>
    history.push(
      `${createPathPrefix({ mode, inviteID })}/${teamSlug}/tracks/${trackSlug}/challenges/${slug}/notes`,
      history.location.state,
    );

  const openConfirmRestart = () => {
    setRestartOpen(true);
    trackAmplitudeEvent({ name: 'Lab - Clicked Restart Track' });
  };

  const onRestartConfirmed = async () => {
    trackAmplitudeEvent({ name: 'Lab - Confirmed Restart Track' });
    const to =
      mode === Mode.Embed
        ? `${pathPrefixByMode(mode)}/${teamSlug}/tracks/${trackSlug}?${new URLSearchParams(
            customParameters || '',
          ).toString()}`
        : addInvitePrefix(`${challenges[0].permalink}/notes?${QueryParams.AUTO_START}=true`, inviteID);

    try {
      await stopTrack({ variables: { trackID: track.id } });
    } catch {
      dispatch(
        'error',
        'Failed to stop the track',
        'Something went wrong while stopping the track, please try again',
        10,
      );
      return;
    }

    // When already on the start page, the modals need to be manually closed
    setRestartOpen(false);
    onClose();

    history.push(to);
  };

  return (
    <>
      <ProgressModal
        trackStatus={trackStatus}
        skippingEnabled={skippingEnabled}
        challenges={challenges}
        currentChallengeId={currentChallenge?.id}
        open={open}
        onClose={onClose}
        onSkip={openConfirmSkip}
        onRestart={openConfirmRestart}
        onView={onView}
        onContinue={onContinue}
        loading={loading}
      />

      <RestartModal open={restartOpen} onClose={() => setRestartOpen(false)} onRestart={onRestartConfirmed} />
      <SkipModal open={confirmOpen} onClose={() => setConfirmOpen(false)} onSkip={onSkipConfirmed} />
      <LoadingModal open={isStopping || isSkipping} message={isStopping ? 'Restarting...' : 'Skipping...'} />
    </>
  );
};

gql`
  mutation StopTrack($trackID: String!) {
    stopTrack(trackID: $trackID) {
      id
      status
      challenges {
        id
        slug
        title
        teaser
        status
      }
    }
  }
`;
